import { PensionsListDTO } from '@components/Home/Pensions/models/PensionListItem.model';
import { InsuranceHistorySummaryDTO } from '@components/InsuranceHistory/models/InsuranceHistorySummaryDTO.model';
import { DisabilityDTO } from '@pages/disability/DisabilityPage.component';
import { API_ENDPOINT } from '@shared/constants/api-endpoints';
import { CurrentContributionsDTO, DebtDTO } from '@shared/models/ContributionsDTO.model';
import { InsuranceHistorySummaryActions } from '@shared/store/insuranceHistorySummary/insuranceHistorySummary';
import { MainMenuActions } from '@shared/store/mainMenu/mainMenu';
import { SummaryActions } from '@shared/store/summary/summary';
import { selectInitialCallHasBeenMade } from '@shared/store/summary/summary.selectors';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFetch } from './useFetch.ts';

export const useInitiateSummaryAndMenu = () => {
  const dispatch = useDispatch();

  const initialCallHasBeenMade = useSelector(selectInitialCallHasBeenMade);

  const {
    get: getCurrentMonthPensions,
    loading: currentMonthPensionsLoading,
    response: currentMonthPensions,
    error: currentMonthPensionsError
  } = useFetch<PensionsListDTO>(API_ENDPOINT.PENSIONS);
  const {
    get: getPreviousMonthPensions,
    loading: previousMonthPensionsLoading,
    response: previousMonthPensions,
    error: previousMonthPensionsError
  } = useFetch<PensionsListDTO>(API_ENDPOINT.PENSIONS);
  const {
    get: getKEAOContributions,
    response: keaoContributions,
    loading: keaoLoading,
    error: keaoError
  } = useFetch<DebtDTO>(API_ENDPOINT.CONTRIBUTIONS_KEAO);
  const {
    get: getContributionsIDIKA,
    response: idikaContributions,
    loading: idikaLoading,
    error: idikaError
  } = useFetch<CurrentContributionsDTO>(API_ENDPOINT.CONTRIBUTIONS_IDIKA);
  const {
    get: getDisability,
    response: disabilities,
    loading: disabilitiesLoading
  } = useFetch<DisabilityDTO>(API_ENDPOINT.DISABILITY);
  const {
    get: getInsuranceHistorySummary,
    response: insuranceHistorySummary,
    loading: insuranceHistorySummaryLoading,
    error: summaryError
  } = useFetch<InsuranceHistorySummaryDTO>(API_ENDPOINT.INSURANCE_HISTORY_SUMMARY);

  // Observe changes related to SUMMARY
  useEffect(() => {
    const pensionsData = currentMonthPensions || previousMonthPensions;
    const pensionsLoading = currentMonthPensionsLoading || previousMonthPensionsLoading;
    const pensionsError = currentMonthPensionsError || previousMonthPensionsError;

    dispatch(SummaryActions.setPensions({ data: pensionsData, loading: pensionsLoading, error: pensionsError }));
  }, [
    dispatch,
    currentMonthPensionsLoading,
    currentMonthPensions,
    currentMonthPensionsError,
    previousMonthPensionsLoading,
    previousMonthPensions,
    previousMonthPensionsError
  ]);

  // Fallback to previous month if current month has no pensions
  useEffect(() => {
    const isLoading = currentMonthPensionsLoading || previousMonthPensionsLoading;
    if (!isLoading && currentMonthPensions && !currentMonthPensions.pensionInfoList?.length && !previousMonthPensions) {
      const now = new Date();
      const previousMonth = new Date(now.getFullYear(), now.getMonth() - 1);

      getPreviousMonthPensions({
        queryParams: { year: previousMonth.getFullYear(), month: previousMonth.getMonth() + 1 }
      });
    }
  }, [
    currentMonthPensionsLoading,
    currentMonthPensions,
    previousMonthPensionsLoading,
    previousMonthPensions,
    getPreviousMonthPensions
  ]);

  useEffect(() => {
    dispatch(SummaryActions.setContributions({ data: idikaContributions, loading: idikaLoading, error: idikaError }));
  }, [dispatch, idikaLoading, idikaContributions, idikaError]);

  useEffect(() => {
    dispatch(SummaryActions.setDebts({ data: keaoContributions, loading: keaoLoading, error: keaoError }));
  }, [dispatch, keaoLoading, keaoContributions, keaoError]);

  useEffect(() => {
    dispatch(
      SummaryActions.setInsuranceHistorySummary({
        data: {
          freelancer: {
            totalFreelancingYears: insuranceHistorySummary?.freelancer.totalFreelancingYears || 0,
            totalFreelancingMonths: insuranceHistorySummary?.freelancer.totalFreelancingMonths || 0,
            totalFreelancingDays: insuranceHistorySummary?.freelancer.totalFreelancingDays || 0
          },
          employee: { totalDays: insuranceHistorySummary?.employee.totalDays || 0 }
        },
        loading: insuranceHistorySummaryLoading,
        error: summaryError
      })
    );
    dispatch(
      InsuranceHistorySummaryActions.setInsuranceHistorySummary({
        data: insuranceHistorySummary,
        loading: insuranceHistorySummaryLoading,
        error: summaryError
      })
    );
  }, [dispatch, insuranceHistorySummaryLoading, insuranceHistorySummary, summaryError]);

  // Observe changes related to MAIN MENU and SERVICES
  useEffect(() => {
    const areContributionsLoading = idikaLoading || keaoLoading;
    dispatch(MainMenuActions.setContributionsLoading(areContributionsLoading));

    const deptsEnabled =
      areContributionsLoading ||
      !!keaoContributions?.settledDebtsList?.length ||
      !!keaoContributions?.unsettledDebtsList?.length ||
      !!idikaContributions?.contributions?.length;

    dispatch(MainMenuActions.setContributionsEnabled(deptsEnabled));
  }, [idikaLoading, keaoLoading, dispatch, keaoContributions, idikaContributions]);

  useEffect(() => {
    dispatch(MainMenuActions.setDisabilitiesLoading(disabilitiesLoading));

    const areDisabilitiesEnabled = disabilitiesLoading || !!disabilities?.disabilityPercentage;
    dispatch(MainMenuActions.setDisabilitiesEnabled(areDisabilitiesEnabled));
  }, [dispatch, disabilitiesLoading, disabilities]);

  const initiateSummaryAndMenu = () => {
    if (initialCallHasBeenMade) return;

    const now = new Date();
    const currentMonth = now.getMonth() + 1; // Months are zero-indexed in JavaScript

    getCurrentMonthPensions({ queryParams: { year: now.getFullYear(), month: currentMonth } });
    getKEAOContributions();
    getDisability();
    getInsuranceHistorySummary();
    dispatch(SummaryActions.setInitialCallHasBeenMade(true));
    getContributionsIDIKA();
  };

  return { initiateSummaryAndMenu };
};
